import React, { useEffect, useState } from 'react'
import { useTranslations } from 'next-intl'
import { filesize } from 'filesize'
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/components/ui/dialog"
import { compress } from '@/utils/image'

interface ImageFileProps {
  file: File
  target?: 'image/webp' | 'image/avif' | undefined
  onCompress(compressFile: File | Error): void
}

export function ImageFile({ file, target, onCompress }: ImageFileProps) {
  const t = useTranslations('TinyImage.ImageFile')
  const [status, setStatus] = useState('Todo')
  const [compressFile, setCompressFile] = useState<File>()

  const url = URL.createObjectURL(file)
  const compressUrl = compressFile && URL.createObjectURL(compressFile)

  useEffect(() => {
    setStatus('Doing')
    compress(file, {
      target
    }).then(compressFile => {
      return compressFile
    }).then(compressFile => {
      if (compressFile) {
        setCompressFile(compressFile)
        onCompress(compressFile)
        setStatus('Done')
      }
    }).catch(e => {
      onCompress(e)
      setStatus('Error')
    })
  }, [file, target, onCompress])

  const handleDownload = () => {
    const a = document.createElement('a')
    a.href = compressUrl || url
    a.download = file.name.replace(/\.\w+$/, '')
    a.click()
  }

  return (
    <Card className="my-2">
      <CardContent className="flex items-center justify-between p-4">
        <div className="flex items-center space-x-4">
          <Dialog>
            <DialogTrigger asChild>
              <img src={url} className="w-10 h-10 rounded-md shadow-md cursor-pointer" alt={file.name} />
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <img src={url} className="w-full h-auto" alt={file.name} />
            </DialogContent>
          </Dialog>
          <div>
            <p className="font-medium truncate">{file.name}</p>
            <p className="text-sm text-gray-500">{filesize(file.size)}</p>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <Badge variant={status === 'Done' ? "default" : status === 'Error' ? "destructive" : "secondary"}>
            {status === 'Doing' ? t('compressing') : status === 'Done' ? t('success') : t('failed')}
          </Badge>

          {compressFile && (
            <>
              <p className="text-sm font-mono text-muted-foreground">{filesize(compressFile.size)}</p>
              <p className="text-sm font-semibold text-green-500">
                {-Math.round((file.size - compressFile.size) / file.size * 100)}%
              </p>
              <Button variant="default" size="sm" onClick={handleDownload}>{t('download')}</Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

export default React.memo(ImageFile, (prevProps, nextProps) => prevProps.file === nextProps.file)
